import { NgClass, AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { fullAutoTuningEnded, fullAutoTuningRunning, fullAutoTuningStopping } from '@app/models/status-resolve';
import { DeviceService } from '@app/services/device.service';
import { ModalService } from '@app/services/modal.service';
import { TuningControllerService } from '@app/services/tuning-controller.service';
import { DeviceParameterFormComponent } from '@app/shared/device-parameter-form/device-parameter-form.component';
import { FullAutoTuningButtonComponent } from '@app/shared/full-auto-tuning-button/full-auto-tuning-button.component';
import { ParameterFormComponent } from '@app/shared/parameter-form/parameter-form.component';
import { RoundFloatPipe } from '@app/shared/round-float.pipe';
import { DeviceParameter } from '@app/store/device-parameter.model';
import { Device } from '@app/store/device.model';
import { TuningControllerParameter } from '@app/store/tuning-controller-parameter.model';
import { TuningController } from '@app/store/tuning-controller.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-velocity-controller-tuning',
  templateUrl: './velocity-controller-tuning.component.html',
  imports: [
    NgClass,
    FullAutoTuningButtonComponent,
    ParameterFormComponent,
    DeviceParameterFormComponent,
    AsyncPipe,
    RoundFloatPipe,
  ],
})
export class VelocityControllerTuningComponent {
  private deviceService = inject(DeviceService);
  private tuningControllerService = inject(TuningControllerService);
  private modalService = inject(ModalService);

  device$: Observable<Device>;
  tuningParameters$: Observable<TuningControllerParameter[]>;
  pidParameters$: Observable<DeviceParameter[]>;

  fullAutoTuningEnded = fullAutoTuningEnded;
  fullAutoTuningRunning = fullAutoTuningRunning;
  fullAutoTuningStopping = fullAutoTuningStopping;

  private _tuningController: TuningController;

  @Input()
  set tuningController(tuningController: TuningController) {
    if (tuningController.id !== this.tuningController?.id) {
      this.device$ = this.deviceService.selectDeviceByDeviceSerialNumber(tuningController.deviceSerialNumber);

      this.pidParameters$ = this.deviceService.selectDeviceParameters(
        { serialNumber: tuningController.deviceSerialNumber },
        'velocityControllerPids',
      );

      this.tuningParameters$ = this.tuningControllerService.selectTuningControllerParameters(tuningController);
    }

    this._tuningController = tuningController;
  }

  get tuningController() {
    return this._tuningController;
  }

  onRangeInputChange(value: any) {
    this.updateAutoTuningParameterValuesAndRequestAutoTuning(value);
  }

  onSetAutoTuningEnabled(autoTuningEnabled: boolean) {
    const id = this.tuningController.id;
    this.tuningControllerService.updateTuningController({ autoTuningEnabled, id });
    if (autoTuningEnabled) {
      this.requestAutoTuning();
    }
  }

  requestAutoTuning() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestControllerAutoTuning(device, 'velocity');
  }

  onStartFullAutoTuning() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestStartVelocityFullAutoTuning(device);
  }

  onStopFullAutoTuning() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestStopFullAutoTuning(device);
  }

  onOpenFullAutoTuningDescription() {
    this.modalService.openDescriptionModal(
      'Velocity controller full auto-tuning',
      'The "Analyze and tune automatically" feature uses identified mechanical model and Drive configuration to design a model of the whole velocity control loop. This model is used to compute optimal velocity controller gains that ensure maximum control loop bandwidth together with safe stability margins. These gains can be used immediately without any extra adjustment.',
    );
  }

  get isFullAutotuningRunning(): boolean {
    const device = getCurrentValue(this.device$);
    return fullAutoTuningRunning(device.fullAutoTuningStatus);
  }

  private updateAutoTuningParameterValuesAndRequestAutoTuning(formValues: any) {
    this.tuningControllerService.updateTuningControllerParameterValues(formValues);
    this.requestAutoTuning();
  }
}
